/*
  Crochetree wishlist sync

  Keeps a logged in customer's wishlist on their Shopify customer record, so it
  follows them between devices. Loads after wishlist.js and does nothing at all
  for signed out visitors, or if the sync endpoint is unreachable: the browser
  copy keeps working either way.

  The protocol, which matters because getting it wrong either loses saved items
  or resurrects deleted ones:

    first time this browser has seen this customer
      -> POST merge. Purely additive, so whatever they saved here while signed
         out joins their account instead of being thrown away.

    every later page load
      -> GET. The account wins. Local edits were already pushed when they were
         made, so anything different here is a change made on another device,
         including a deletion.

    a change made here
      -> POST replace. The browser is authoritative, so removals propagate.

    a different customer than last time (shared computer)
      -> wipe the local list first, then GET, so one person's wishlist never
         leaks into another person's account.
*/
(function () {
  'use strict';

  var config = window.CrochetreeWishlistConfig || {};
  var wishlist = window.CrochetreeWishlist;

  if (!wishlist || !config.customerId || !config.proxy) return;

  var OWNER_KEY = 'crochetree:wishlist:owner';
  var LIST_KEY = 'crochetree:wishlist:v1';
  var PUSH_DELAY = 900;

  var customerId = String(config.customerId);
  var pushTimer = null;
  var reconciled = false;

  function readOwner() {
    try {
      return window.localStorage.getItem(OWNER_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeOwner() {
    try {
      window.localStorage.setItem(OWNER_KEY, customerId);
    } catch (e) {
      /* storage unavailable: sync degrades to per page load, nothing breaks */
    }
  }

  function adopt(payload) {
    if (!payload || !payload.signedIn || !Array.isArray(payload.items)) return false;
    /* marking it remote stops this write from bouncing straight back at the server */
    wishlist.replaceAll(payload.items, 'remote');
    return true;
  }

  function pull() {
    return fetch(config.proxy, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('sync ' + response.status);
        return response.json();
      })
      .then(adopt)
      .catch(function () {
        return false;
      });
  }

  function push(mode) {
    return fetch(config.proxy, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ mode: mode, items: wishlist.read() })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('sync ' + response.status);
        return response.json();
      })
      .then(adopt)
      .catch(function () {
        return false;
      });
  }

  function reconcile() {
    var owner = readOwner();

    if (owner && owner !== customerId) {
      /* someone else was signed in here before: their list is not ours to upload */
      wishlist.replaceAll([], 'remote');
    }

    /* only a browser that has never synced with any account may push its list up */
    var opening = owner ? pull() : push('merge');

    return opening.then(function (ok) {
      /*
        Only start pushing authoritative writes once we know what the account
        holds. Pushing `replace` off a stale list would delete their saved items.
      */
      reconciled = ok;
      if (ok) writeOwner();
      return ok;
    });
  }

  function schedulePush() {
    if (!reconciled) return;
    window.clearTimeout(pushTimer);
    pushTimer = window.setTimeout(function () {
      push('replace');
    }, PUSH_DELAY);
  }

  document.addEventListener('crochetree:wishlist:updated', function (event) {
    if (event.detail && event.detail.source === 'remote') return;
    schedulePush();
  });

  /* a change made in another tab is already in storage, so just publish it */
  window.addEventListener('storage', function (event) {
    if (event.key === LIST_KEY) schedulePush();
  });

  reconcile();
})();
